import { NagraProduct, UserEntitlement } from '../models'
import { Subscription, TVODProduct, Offer } from 'models'
/**
 * Parse Nagra products
 * @return {Object} data raw data from the backend
 * @param {Boolean} useExternalOffer Offer are described by another API not provided by Nagra
 * @return {Subscription|TVODProduct} Nagra TV/VOD Product
 */
export const ProductFactory = (data = {}, useExternalOffer = true) => {
    const nagraProduct = new NagraProduct({
        ...data,
    })
    const {
        id: productId,
        title,
        title2,
        description,
        currency,
        price,
        startValidity,
        endValidity,
        purchaseDurationMs,
        rentalDurationMs,
    } = nagraProduct
    const props = {
        id: productId,
        programId: productId,
        name: title || title2,
        title: title || title2,
        description: description,
        currency: currency,
        validityStart: startValidity,
        validityEnd: endValidity,
        isAvailable: true,
        offers: useExternalOffer
            ? []
            : [
                  new Offer({
                      offerId: productId,
                      title: title || title2,
                      period:
                          nagraProduct.type === NagraProduct.TYPE.TRANSACTIONAL
                              ? rentalDurationMs
                              : purchaseDurationMs,
                      periodUnit: Offer.UNIT.MS,
                      price: price,
                      originalPrice: price,
                      currency: currency,
                  }),
              ],
    }
    if (nagraProduct.type === NagraProduct.TYPE.TRANSACTIONAL) {
        return new TVODProduct({ ...props })
    } else {
        return new Subscription({ ...props })
    }
}

/**
 * Consolidate Nagra subscriptions
 * @param {NagraProduct} VODProducts Nagra VOD Products
 * @param {NagraProduct} BTVProducts Nagra BTV Products
 * @param {NagraProduct} Merged Nagra Products
 */
export const mergeProducts = (VODProducts = [], BTVProducts = []) => {
    // Remove duplicated item after getting clarification from nagra
    // Nagra explanation:
    // on our side, the product is a single entity that doesn't classify whether is vod / btv product type.
    // when a product is attached to only VOD it will be appear on the /vod/product as well as when a product is attached to a channel.
    // so when the product exists and it does not belong to any VOD/BTV it will appear on both API results.
    return Object.values(
        [...VODProducts, ...BTVProducts].filter(Boolean).reduce((acc, product) => {
            const productId = product.id
            if (!acc[productId]) {
                acc[productId] = product
            }
            return acc
        }, {})
    )
}

/**
 * Update entitlements into Products
 * @return {Object} data raw data from the backend
 * @param {Array<TVODProduct|Subscription>} products  TVOD Products or Subscriptions
 * @param {Array<TVODProduct|Subscription>} Products updated with entitlements
 * @param {Array<TVODProduct|Subscription>} userPaymentMethods updated with entitlements
 */
export const updateProductsEntitlements = (
    products = [],
    userEntitlements = [],
    userPaymentMethods = []
) => {
    // To reduce computation complexity, create an intermediary object
    const _userEntitlements = (userEntitlements || [])
        // Keep only Entitled product as a security to handle duplicated past entitlement which became not entitled
        .filter((x) => x && UserEntitlement.STATUS.SUBSCRIBED === x.status)
        .reduce((acc, entitlement) => {
            if (entitlement && entitlement.productId) {
                acc[entitlement.productId] = entitlement
            }
            return acc
        }, {})

    const productsWithEntitlements = (products || []).reduce((acc, product) => {
        if (product && product.id) {
            let props = {
                // isAvailable: true, Availability retrieved from Proxy through product_status
                isEntitled: false,
                offers: (product.offers || []).filter(
                    (x) => x.isPaymentMethodAllowed && x.isPaymentMethodAllowed(userPaymentMethods)
                ),
            }
            if (_userEntitlements[product.id]) {
                const { status, validFrom, expiryDate, transactionId } =
                    _userEntitlements[product.id]
                props = {
                    isEntitled: UserEntitlement.STATUS.SUBSCRIBED === status,
                    entitlementStart: validFrom,
                    entitlementEnd: expiryDate,
                    transactionId: transactionId,
                }
            }
            acc.push(product.update(props))
        }

        return acc
    }, [])

    return productsWithEntitlements
}
